import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ShareModal from '../components/ShareModal';
import './Notebook.css';

function Notebook({ user }) {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [showShareModal, setShowShareModal] = useState(false);

    // Initialize from location state if coming from IDE
    const initialContent = location.state?.content || '# Welcome to NeuAIs Colab\nimport torch\nprint(f"PyTorch version: {torch.__version__}")';
    const initialName = location.state?.fileName ? location.state.fileName.replace(/\\/g, ' - ') : 'Untitled Notebook';

    const [cells, setCells] = useState([
        { id: '1', type: 'code', content: initialContent, output: '', executing: false },
        { id: '2', type: 'code', content: '', output: '', executing: false }
    ]);
    const [notebookName, setNotebookName] = useState(initialName);
    const [resources, setResources] = useState({
        gpuMemory: 45,
        cpuUsage: 23,
        uptime: '2h 15m'
    });

    const executeCell = (cellId) => {
        setCells(cells.map(cell =>
            cell.id === cellId ? { ...cell, executing: true } : cell
        ));

        setTimeout(() => {
            setCells(cells.map(cell =>
                cell.id === cellId
                    ? { ...cell, executing: false, output: 'PyTorch version: 2.1.1\nCUDA available: True' }
                    : cell
            ));
        }, 1500);
    };

    const updateCellContent = (cellId, content) => {
        setCells(cells.map(cell =>
            cell.id === cellId ? { ...cell, content } : cell
        ));
    };

    const deleteCell = (cellId) => {
        setCells(cells.filter(cell => cell.id !== cellId));
    };

    const addCell = () => {
        const newCell = {
            id: Date.now().toString(),
            type: 'code',
            content: '',
            output: '',
            executing: false
        };
        setCells([...cells, newCell]);
    };

    const saveNotebook = () => {
        console.log('Saving notebook...', { notebookName, cells });
    };

    const handleOpenInIDE = () => {
        // Combine all cells into a single file to open in IDE
        const combinedContent = cells.map((cell, index) =>
            `# Cell ${index + 1}\n${cell.content}\n`
        ).join('\n');

        navigate('/ide', {
            state: {
                fromNotebook: true,
                fileName: notebookName.replace(/\s+/g, '_') + '.py',
                content: combinedContent,
            }
        });
    };

    return (
        <div className="notebook-page">
            {/* Toolbar */}
            <div className="notebook-toolbar">
                <div className="notebook-title-section">
                    <img src="/svg/folder.svg" alt="Notebook" style={{ width: '20px' }} />
                    <input
                        type="text"
                        value={notebookName}
                        onChange={(e) => setNotebookName(e.target.value)}
                        className="notebook-title-input"
                    />
                    <div className="session-status">
                        <span className="status-indicator"></span>
                        Connected
                    </div>
                </div>

                <div className="toolbar-actions">
                    <button onClick={saveNotebook} className="toolbar-btn">
                        <img src="/svg/copy-files.svg" alt="Save" />
                        Save
                    </button>
                    <button onClick={() => setShowShareModal(true)} className="toolbar-btn">
                        <img src="/svg/link-white.svg" alt="Share" />
                        Share
                    </button>
                    <button onClick={handleOpenInIDE} className="toolbar-btn">
                        <img src="/svg/code.svg" alt="IDE" />
                        Open in IDE
                    </button>
                    <button onClick={addCell} className="toolbar-btn primary">
                        <img src="/svg/white-plus.svg" alt="Add" />
                        Code
                    </button>
                </div>
            </div>

            <div className="notebook-container">
                {/* Main Content */}
                <div className="notebook-content">
                    <div className="notebook-cells">
                        {cells.map((cell) => (
                            <div key={cell.id} className={`code-cell ${cell.executing ? 'executing' : ''}`}>
                                <div className="cell-toolbar">
                                    <button
                                        onClick={() => executeCell(cell.id)}
                                        className="cell-run-btn"
                                        disabled={cell.executing}
                                    >
                                        {cell.executing ? (
                                            <img src="/svg/stop-profiling.svg" alt="Running" />
                                        ) : (
                                            <img src="/svg/play-circle.svg" alt="Run" />
                                        )}
                                    </button>

                                    <div className="cell-actions">
                                        <button className="cell-action-btn">
                                            <img src="/svg/arrows-move.svg" alt="Move" />
                                        </button>
                                        <button className="cell-action-btn" onClick={() => deleteCell(cell.id)}>
                                            <img src="/svg/warning.svg" alt="Delete" />
                                        </button>
                                    </div>
                                </div>

                                <textarea
                                    className="cell-input"
                                    value={cell.content}
                                    onChange={(e) => updateCellContent(cell.id, e.target.value)}
                                    placeholder="Enter your code here..."
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && e.shiftKey) {
                                            e.preventDefault();
                                            executeCell(cell.id);
                                        }
                                    }}
                                />

                                {cell.output && (
                                    <div className="cell-output">
                                        {cell.output}
                                    </div>
                                )}
                            </div>
                        ))}

                        <button onClick={addCell} className="add-cell-btn">
                            <img src="/svg/plus.svg" alt="Add" />
                            Add Code Cell
                        </button>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="notebook-sidebar">
                    <div className="sidebar-section">
                        <h3>Resources</h3>
                        <div className="resource-item">
                            <div className="resource-label">
                                <img src="/svg/database-cog.svg" alt="GPU" />
                                GPU Memory
                            </div>
                            <div className="resource-value">{resources.gpuMemory}%</div>
                        </div>
                        <div className="resource-bar">
                            <div className="resource-bar-fill" style={{ width: `${resources.gpuMemory}%` }}></div>
                        </div>

                        <div className="resource-item" style={{ marginTop: '12px' }}>
                            <div className="resource-label">
                                <img src="/svg/cog.svg" alt="CPU" />
                                CPU Usage
                            </div>
                            <div className="resource-value">{resources.cpuUsage}%</div>
                        </div>
                        <div className="resource-bar">
                            <div className="resource-bar-fill" style={{ width: `${resources.cpuUsage}%` }}></div>
                        </div>

                        <div className="resource-item" style={{ marginTop: '12px' }}>
                            <div className="resource-label">
                                <img src="/svg/graph.svg" alt="Uptime" />
                                Session Uptime
                            </div>
                            <div className="resource-value">{resources.uptime}</div>
                        </div>
                    </div>

                    <div className="sidebar-section">
                        <h3>Files</h3>
                        <ul className="file-list">
                            <li className="file-item">
                                <img src="/svg/white-folder.svg" alt="Folder" />
                                <span>sample_data/</span>
                            </li>
                            <li className="file-item">
                                <img src="/svg/code.svg" alt="File" />
                                <span>notebook.ipynb</span>
                            </li>
                            <li className="file-item">
                                <img src="/svg/code.svg" alt="File" />
                                <span>requirements.txt</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Share Modal */}
            {showShareModal && (
                <ShareModal
                    notebookId={sessionId || 'demo-notebook'}
                    notebookName={notebookName}
                    onClose={() => setShowShareModal(false)}
                />
            )}
        </div>
    );
}

export default Notebook;
